import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Settings, Star, MapPin } from "lucide-react"
import { format } from "date-fns"
import { useAppSelector, useAppDispatch } from "@/app/hooks"
import {
  selectIsAuthenticated,
  selectCurrentUser,
  logOut,
  setLoginSheetOpen,
  updateUserProfile,
  updateUserProfileImage,
} from "@/features/auth/authSlice"
import { getReviewsByUserId, getSiteById, deleteReview } from "@/services/api"
import { fetchSitesByIds, selectBookmarkedSites, selectBookmarkedSitesStatus } from "@/features/maps/mapsSlice"
import { Avatar } from "@/components/ui"
import type { ReviewResponse } from "@/types/review"
import "./ProfilePage.css"

export const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectCurrentUser)

  // Bookmarks state
  const bookmarkedSites = useAppSelector(selectBookmarkedSites)
  const bookmarksStatus = useAppSelector(selectBookmarkedSitesStatus)
  const [bookmarkIds, setBookmarkIds] = useState<string[]>([])

  // Reviews state
  const [reviews, setReviews] = useState<ReviewResponse[]>([])
  const [reviewsLoading, setReviewsLoading] = useState<boolean>(false)
  const [siteNames, setSiteNames] = useState<Record<string, string>>({})

  // Tabs state
  const [activeTab, setActiveTab] = useState<"reviews" | "bookmarks">("reviews")

  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({ name: "", nickname: "" })
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(setLoginSheetOpen(true))
    }
  }, [isAuthenticated, dispatch])

  useEffect(() => {
    if (user) {
      setEditForm({ name: user.name, nickname: user.nickname })
    }
  }, [user])

  // Fetch reviews when tab changes to reviews
  useEffect(() => {
    if (user && activeTab === "reviews") {
      const fetchUserReviews = async () => {
        setReviewsLoading(true)
        try {
          const response = await getReviewsByUserId(user.id)
          setReviews(response.content)
        } catch (error) {
          console.error("Failed to fetch user reviews:", error)
        } finally {
          setReviewsLoading(false)
        }
      }
      void fetchUserReviews()
    }
  }, [user, activeTab])

  // Fetch campsite names for the reviews
  useEffect(() => {
    if (reviews.length === 0) return

    const fetchSiteNames = async () => {
      const uniqueSiteIds = Array.from(new Set(reviews.map(r => r.campingSiteId)))
      const names: Record<string, string> = {}
      
      await Promise.all(
        uniqueSiteIds.map(async id => {
          try {
            const site = await getSiteById(id)
            names[id] = site.name
          } catch (error) {
            console.error(`Failed to fetch site name for ${id}:`, error)
            names[id] = "알 수 없는 캠핑장"
          }
        }),
      )
      setSiteNames(names)
    }

    void fetchSiteNames()
  }, [reviews])

  // Fetch bookmarked sites when tab changes to bookmarks
  useEffect(() => {
    if (activeTab === "bookmarks") {
      const ids = JSON.parse(localStorage.getItem("bookmarks") ?? "[]") as string[]
      setBookmarkIds(ids)
      if (ids.length > 0 && bookmarkedSites.length === 0) {
        void dispatch(fetchSitesByIds(ids))
      }
    }
  }, [activeTab, dispatch, bookmarkedSites.length])

  const handleLogout = () => {
    dispatch(logOut())
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing)
    if (user) {
      setEditForm({ name: user.name, nickname: user.nickname })
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setEditForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    if (!user) return

    try {
      await dispatch(
        updateUserProfile({
          name: editForm.name,
          nickname: editForm.nickname,
        }),
      ).unwrap()

      setIsEditing(false)
      alert("프로필이 성공적으로 업데이트되었습니다.")
    } catch (error) {
      console.error("프로필 업데이트 실패:", error)
      alert("프로필 업데이트에 실패했습니다. 다시 시도해주세요.")
    }
  }

  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      try {
        await dispatch(updateUserProfileImage(file)).unwrap()
        alert("프로필 이미지가 성공적으로 변경되었습니다.")
      } catch (error) {
        console.error("프로필 이미지 변경 실패:", error)
        alert("프로필 이미지 변경에 실패했습니다. 다시 시도해주세요.")
      }
    }
  }

  const handleDeleteReview = async (reviewId: string) => {
    if (window.confirm("정말로 이 리뷰를 삭제하시겠습니까?")) {
      try {
        await deleteReview(reviewId)
        setReviews(prev => prev.filter(r => r.id !== reviewId))
        alert("리뷰가 삭제되었습니다.")
      } catch (error) {
        console.error("Failed to delete review:", error)
        alert("리뷰 삭제에 실패했습니다. 다시 시도해 주세요.")
      }
    }
  }

  // 1. 로그인하지 않은 경우, 로그인 시트가 열리는 동안 보여줄 내용
  if (!isAuthenticated) {
    return <div className="profile-container">로그인이 필요합니다.</div>
  }

  // 2. 로그인은 했지만, 아직 사용자 정보를 불러오는 중인 경우
  if (!user) {
    return <div className="profile-container">프로필 정보를 불러오는 중...</div>
  }

  // 3. 사용자 정보 로딩이 완료된 경우
  return (
    <div className="profile-container">
      <button
        className="settings-icon-button"
        onClick={() => { void navigate("/settings"); }}
        aria-label="설정"
      >
        <Settings size={24} />
      </button>
      <div className="profile-header">
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => { void handleFileChange(e); }}
          style={{ display: "none" }}
          accept="image/*"
        />
        <Avatar
          src={user.profileImageUrl}
          alt={user.name}
          className="profile-image"
          onClick={handleImageClick}
          style={{ cursor: "pointer" }}
        />
        {isEditing ? (
          <div className="profile-edit-form">
            <input
              type="text"
              name="name"
              value={editForm.name}
              onChange={handleInputChange}
              className="profile-input"
            />
            <input
              type="text"
              name="nickname"
              value={editForm.nickname}
              onChange={handleInputChange}
              className="profile-input"
            />
            <div className="profile-edit-actions">
              <button onClick={() => { void handleSave(); }} className="profile-button save">
                저장
              </button>
              <button
                onClick={handleEditToggle}
                className="profile-button cancel"
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="profile-name">
              {user.name} ({user.nickname})
            </h2>
            <p className="profile-email">{user.email}</p>
            <button onClick={handleEditToggle} className="profile-button edit-btn">
              프로필 수정
            </button>
          </>
        )}
      </div>

      <div className="profile-content">
        <div className="profile-tabs">
          <button
            type="button"
            className={`profile-tab ${activeTab === "reviews" ? "active" : ""}`}
            onClick={() => { setActiveTab("reviews"); }}
          >
            내가 쓴 리뷰
          </button>
          <button
            type="button"
            className={`profile-tab ${activeTab === "bookmarks" ? "active" : ""}`}
            onClick={() => { setActiveTab("bookmarks"); }}
          >
            북마크 한 캠핑장
          </button>
        </div>

        <div className="profile-tab-pane">
          {activeTab === "reviews" && (
            <div className="profile-reviews-pane">
              {reviewsLoading ? (
                <div className="profile-pane-loading">리뷰를 불러오는 중...</div>
              ) : reviews.length === 0 ? (
                <div className="profile-pane-empty">작성한 리뷰가 없습니다.</div>
              ) : (
                <div className="profile-reviews-list">
                  {reviews.map(review => (
                    <div key={review.id} className="profile-review-card">
                      <div className="review-card-header">
                        <Link to={`/sites/${review.campingSiteId}`} className="site-link">
                          {siteNames[review.campingSiteId] || "로딩 중..."}
                        </Link>
                        <button
                          type="button"
                          className="review-delete-btn"
                          onClick={() => { void handleDeleteReview(review.id); }}
                          aria-label="리뷰 삭제"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="review-card-meta">
                        <div className="card-stars">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i.toString()}
                              size={12}
                              fill={i < review.rating ? "currentColor" : "none"}
                              strokeWidth={i < review.rating ? 0 : 1.5}
                              color="var(--color-warning-500)"
                            />
                          ))}
                        </div>
                        <span className="card-date">
                          {format(new Date(review.createdAt), "yyyy.MM.dd")}
                        </span>
                      </div>
                      <p className="review-comment-text">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "bookmarks" && (
            <div className="profile-bookmarks-pane">
              {bookmarksStatus === "loading" && bookmarkedSites.length === 0 ? (
                <div className="profile-pane-loading">북마크를 불러오는 중...</div>
              ) : bookmarkIds.length === 0 ? (
                <div className="profile-pane-empty">북마크한 캠핑장이 없습니다.</div>
              ) : (
                <div className="profile-bookmarks-list">
                  {bookmarkedSites.map(site => (
                    <Link
                      key={site.id}
                      to={`/sites/${site.id}`}
                      className="profile-bookmark-item"
                    >
                      {site.imageUrls.length > 0 ? (
                        <img
                          src={site.imageUrls[0]}
                          alt={site.name}
                          className="bookmark-thumb"
                        />
                      ) : (
                        <div className="bookmark-thumb-placeholder">
                          <MapPin size={20} />
                        </div>
                      )}
                      <div className="bookmark-info">
                        <h4 className="bookmark-title">{site.name}</h4>
                        <p className="bookmark-address-text">{site.address}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <button onClick={handleLogout} className="logout-button">
        로그아웃
      </button>
    </div>
  )
}
